import { useEffect, useRef, useState } from "react";
import { Loader2, Square, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const cache = new Map();
let current = null;

async function audioUrl(text) {
  if (cache.has(text)) return cache.get(text);
  const r = await api.post("/tts", { text }, { responseType: "blob", timeout: 90000 });
  const url = URL.createObjectURL(r.data);
  cache.set(text, url);
  return url;
}

async function blobError(e) {
  const d = e?.response?.data;
  if (d instanceof Blob) {
    try { return JSON.parse(await d.text()).detail; } catch (_) { return null; }
  }
  return null;
}

export function ListenButton({ text, label = "Listen", testid = "listen-btn", className = "" }) {
  const [state, setState] = useState("idle");
  const audio = useRef(null);
  useEffect(() => () => { if (audio.current) audio.current.pause(); }, []);
  const clean = (text || "").trim().slice(0, 12000);
  if (!clean) return null;
  const stop = () => { audio.current?.pause(); setState("idle"); };
  const play = async () => {
    if (state === "playing") return stop();
    if (state === "loading") return;
    setState("loading");
    try {
      const url = await audioUrl(clean);
      if (current && current !== audio.current) current.pause();
      const a = new Audio(url);
      audio.current = a;
      current = a;
      a.onended = () => setState("idle");
      a.onpause = () => setState("idle");
      await a.play();
      setState("playing");
    } catch (e) {
      setState("idle");
      toast.error((await blobError(e)) || "Couldn't play audio. Please try again.");
    }
  };
  const Icon = state === "loading" ? Loader2 : state === "playing" ? Square : Volume2;
  return (
    <button type="button" onClick={play} data-testid={testid} aria-pressed={state === "playing"} aria-label={state === "playing" ? "Stop reading" : label}
      className={`no-print inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-[#E6E1D7] bg-white text-xs font-medium text-stone-700 hover:bg-stone-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900/30 ${className}`}>
      <Icon className={`h-3.5 w-3.5 ${state === "loading" ? "animate-spin" : ""}`} />
      {state === "playing" ? "Stop" : state === "loading" ? "Preparing…" : label}
    </button>
  );
}
